'use client';

import { Footer } from '@/components/public/footer';
import { NavPreview } from '@/components/public/nav-preview';
import { useScrollReveal } from '@/lib/hooks/use-scroll-reveal';
import { ArrowRight } from 'lucide-react';
import Image from 'next/image';
import { Link } from '@/i18n/navigation';
import { useTranslations } from 'next-intl';
import { FaqAccordion } from '@/components/public/faq-accordion';
import { FAQ_CATEGORIES } from '@/lib/faq-content';
import { useFaqItems } from '@/lib/faq-hooks';

function FAQAccordion() {
  const category = FAQ_CATEGORIES.find((c) => c.id === 'autumnOffer2026')!;
  const items = useFaqItems(category);
  return <FaqAccordion items={items} accent={category.accent} />;
}

/**
 * PYN rate-plan id for "Family with SPA" (30% advance) — the plan this offer's
 * copy tells the guest to pick. Passed as `?plan=` so arriving from this page
 * pre-selects it instead of leaving them on checkout's cheapest-plan default.
 * Advisory end to end: it is applied only if the plan is actually available for
 * the chosen rooms and dates (PYN decides), otherwise checkout falls back to
 * the cheapest plan. Keep in sync with `rate_plans.pyn_plan_id`.
 */
const OFFER_PLAN_ID = '14';
const OFFER_BOOKING_HREF = `/rooms?plan=${OFFER_PLAN_ID}`;

export default function Autumn2026OfferPage() {
  const t = useTranslations('design.autumnOffer2026');

  useScrollReveal();

  const terms = [1, 2, 3, 4, 5, 6].map((i) => t(`terms.item${i}`));

  return (
    <main className="min-h-screen bg-white text-gray-900">
      <NavPreview />

      {/* Hero Section */}
      <section className="relative h-screen w-full overflow-hidden">
        <Image
          src="/design-images/uploads/1788776246823-2hls6j-d_05.jpg"
          alt={t('hero.imageAlt')}
          fill
          className="object-cover"
          priority
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/25 to-black/30" />
        <div className="relative h-full flex flex-col items-center justify-center px-6 text-center">
          <div className="observe-me slide-up w-full max-w-3xl">
            <div className="inline-block px-6 py-2 bg-[#801515] text-white text-sm font-semibold rounded-full mb-8 uppercase tracking-wider">
              {t('hero.badge')}
            </div>
            <h1 className="font-poppins text-3xl sm:text-5xl md:text-7xl font-bold text-white mb-8 tracking-tight break-words" style={{ textShadow: '2px 2px 30px rgba(0, 0, 0, 0.5)' }}>
              {t('hero.title')}
            </h1>
            <div className="inline-block px-8 py-4 bg-white/95 text-[#801515] text-xl md:text-2xl font-bold rounded-lg shadow-lg tracking-wide">
              {t('hero.subtitle')}
            </div>
            {/* Above-the-fold booking CTA: the offer's first affordance used to
                sit a full screen down, past the hero. */}
            <div className="mt-10">
              <Link
                href={OFFER_BOOKING_HREF}
                className="group px-[26px] py-4 xl:py-5 bg-[#801515] hover:bg-[#601010] text-white font-serif text-lg xl:text-xl rounded-full transition-all duration-500 inline-flex items-center gap-3 shadow-lg hover:shadow-xl hover:shadow-[#801515]/30"
              >
                {t('hero.cta')}
                <ArrowRight className="w-6 h-6 group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Family Highlight Section - Asymmetric Layout (Image 3/5, Text 2/5) */}
      <section className="grid lg:grid-cols-5 min-h-screen design-section-gap rounded-3xl overflow-hidden">
        {/* Image Side - 3 columns */}
        <div className="lg:col-span-3 relative h-[50vh] lg:h-auto overflow-hidden bg-gray-100 order-2 lg:order-1 observe-me slide-left">
          <Image
            src="/design-images/uploads/1788779370192-vbuutq-dsc08418.jpg"
            alt={t('familyHighlight.imageAlt')}
            fill
            className="object-cover"
          />
        </div>

        {/* Text Side - 2 columns */}
        <div className="lg:col-span-2 flex items-center justify-center design-content-pad lg:p-12 xl:p-16 bg-[#faf8f6] order-1 lg:order-2">
          <div className="max-w-lg observe-me slide-right">
            <h2 className="font-poppins text-[clamp(2.25rem,3.5vw,4rem)] font-bold mb-6 leading-[1.1]">
              <span className="text-[#801515]">{t('familyHighlight.heading')}</span>
              {(() => {
                const subheading = t('familyHighlight.subheading');
                if (!subheading) return null;
                return (
                  <>
                    <br />
                    <span className="text-gray-800 text-xl sm:text-3xl md:text-[clamp(1.75rem,2.8vw,3rem)] font-normal mt-3 block">
                      {subheading}
                    </span>
                  </>
                );
              })()}
            </h2>
            <div className="w-16 h-1 bg-[#801515] mb-6" />
            <p className="text-lg xl:text-xl text-gray-700 leading-relaxed mb-4">
              <span className="text-[#801515] italic">{t('familyHighlight.dateRange')}</span>
            </p>
            <p className="text-lg xl:text-xl text-gray-700 leading-relaxed mb-4">
              {t('familyHighlight.p1')}
            </p>
            <p className="text-lg xl:text-xl text-gray-700 leading-relaxed mb-4">
              {t('familyHighlight.p2')}
            </p>
            <p className="text-lg xl:text-xl text-gray-700 leading-relaxed mb-8">
              {t.rich('familyHighlight.p3', { strong: (chunks) => <strong>{chunks}</strong> })}
            </p>
            <div>
              <Link
                href={OFFER_BOOKING_HREF}
                className="group px-[26px] py-5 xl:py-6 bg-[#801515] hover:bg-[#601010] text-white font-serif text-xl xl:text-2xl rounded-full transition-all duration-500 inline-flex items-center gap-3 shadow-lg hover:shadow-xl hover:shadow-[#801515]/30"
              >
                {t('familyHighlight.cta')}
                <ArrowRight className="w-7 h-7 group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Activities Section */}
      <section className="design-section-gap rounded-3xl overflow-hidden bg-white">
        <div className="max-w-4xl mx-auto design-content-pad lg:py-20">
          <div className="observe-me slide-up space-y-8">
            <h2 className="font-poppins text-3xl xl:text-4xl font-bold text-gray-900 text-center">
              {t('activities.heading')}
            </h2>
            <div className="space-y-4 text-lg xl:text-xl text-gray-700 leading-relaxed">
              <p>{t('activities.p1')}</p>
              <p>{t('activities.p2')}</p>
              <p className="font-semibold text-[#801515]">{t('activities.p3')}</p>
              <p>{t('activities.p4')}</p>
            </div>
          </div>

          {/* Activity Images Grid */}
          <div className="grid md:grid-cols-3 gap-6 mt-12">
            {[
              { src: '/design-images/uploads/1788780850620-siffd3-whatsapp-image-2024-10-30-at-17.53.56-1-.jpg', label: t('activities.item1') },
              { src: '/design-images/uploads/1788780850816-uuhb14-vladimir-zeki-589-of-790-.jpg', label: t('activities.item2') },
              { src: '/design-images/uploads/1788780850685-40rptk-p_11.jpg', label: t('activities.item3') },
            ].map((item, i) => (
              <div key={i} className="observe-me slide-up">
                <div className="relative h-64 md:h-72 overflow-hidden rounded-2xl shadow-md">
                  <Image
                    src={item.src}
                    alt={item.label}
                    fill
                    className="object-cover"
                  />
                </div>
                <p className="text-center text-lg font-semibold text-gray-800 mt-4">
                  {item.label}
                </p>
              </div>
            ))}
          </div>

          {/* Programme Schedule Table */}
          <div className="mt-14 observe-me slide-up">
            <h3 className="font-poppins text-2xl xl:text-3xl font-bold text-gray-900 text-center mb-8">
              {t('activities.schedule.heading')}
            </h3>
            {(() => {
              const columns = [
                {
                  name: t('activities.item1'),
                  dates: [t('activities.schedule.pumpkinDate1'), t('activities.schedule.pumpkinDate2'), t('activities.schedule.pumpkinDate3')],
                  times: [t('activities.schedule.pumpkinTime1'), t('activities.schedule.pumpkinTime2')],
                  desc: t('activities.schedule.pumpkinDesc'),
                },
                {
                  name: t('activities.item2'),
                  dates: [t('activities.schedule.treasureDate1'), t('activities.schedule.treasureDate2'), t('activities.schedule.treasureDate3')],
                  times: [t('activities.schedule.treasureTime')],
                  desc: t('activities.schedule.treasureDesc'),
                },
                {
                  name: t('activities.item3'),
                  dates: [t('activities.schedule.bonfireDate1'), t('activities.schedule.bonfireDate2'), t('activities.schedule.bonfireDate3'), t('activities.schedule.bonfireDate4')],
                  times: [t('activities.schedule.bonfireTime')],
                  desc: t('activities.schedule.bonfireDesc'),
                },
              ];
              return (
                <div className="rounded-2xl border border-[#801515]/15 shadow-sm overflow-hidden bg-white">
                  {/* Desktop: one column per activity, in the same order as the photos above */}
                  <table className="hidden md:table w-full text-left text-base xl:text-lg text-gray-700 table-fixed">
                    <thead className="bg-[#801515] text-white">
                      <tr>
                        {columns.map((col, i) => (
                          <th key={i} scope="col" className="px-5 py-3 font-poppins font-semibold">{col.name}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {Array.from({ length: Math.max(...columns.map((c) => c.dates.length)) }).map((_, row) => (
                        <tr key={row} className="bg-white">
                          {columns.map((col, i) => (
                            <td key={i} className={`px-5 pb-0 align-top font-medium text-gray-900 leading-snug ${row === 0 ? 'pt-3' : 'pt-1'}`}>{col.dates[row] || ''}</td>
                          ))}
                        </tr>
                      ))}
                      <tr className="bg-[#faf8f6]">
                        {columns.map((col, i) => (
                          <td key={i} className="px-5 py-4 align-top">
                            {col.times.map((time, j) => (
                              <span key={j} className="block">{time}</span>
                            ))}
                          </td>
                        ))}
                      </tr>
                      <tr className="bg-white">
                        {columns.map((col, i) => (
                          <td key={i} className="px-5 py-4 align-top text-gray-600">{col.desc}</td>
                        ))}
                      </tr>
                    </tbody>
                  </table>

                  {/* Mobile: one stacked card per activity */}
                  <div className="md:hidden">
                    {columns.map((col, i) => (
                      <div key={i} className="px-5 py-4 border-t first:border-t-0 border-[#801515]/10 odd:bg-white even:bg-[#faf8f6]">
                        <p className="font-poppins font-semibold text-[#801515]">{col.name}</p>
                        <div className="font-medium text-gray-900 mt-1">
                          {col.dates.map((date, j) => (
                            <span key={j} className="block">{date}</span>
                          ))}
                        </div>
                        <div>
                          {col.times.map((time, j) => (
                            <span key={j} className="block">{time}</span>
                          ))}
                        </div>
                        <p className="text-gray-600 mt-1">{col.desc}</p>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })()}
            {/* Daily SPA moments - separate from the table, two columns */}
            <div className="mt-8 rounded-2xl border border-[#801515]/15 shadow-sm bg-[#faf8f6] px-5 py-6 md:px-8">
              <h4 className="font-poppins text-xl xl:text-2xl font-bold text-[#801515] text-center mb-6">
                {t('activities.schedule.spaHeading')}
              </h4>
              <div className="grid md:grid-cols-2 gap-6 md:gap-10 text-base xl:text-lg">
                {[
                  { label: t('activities.schedule.spaFamilyLabel'), time: t('activities.schedule.spaFamilyTime'), desc: t('activities.schedule.spaFamilyDesc') },
                  { label: t('activities.schedule.spaAdultsLabel'), time: t('activities.schedule.spaAdultsTime'), desc: t('activities.schedule.spaAdultsDesc') },
                ].map((col, i) => (
                  <div key={i} className="text-center md:text-left">
                    <p className="font-semibold text-gray-900">{col.label} · {col.time}</p>
                    <p className="text-gray-600 mt-1">{col.desc}</p>
                  </div>
                ))}
              </div>
            </div>
            <p className="text-sm xl:text-base text-gray-600 italic text-center mt-4">
              {t('activities.schedule.note')}
            </p>
          </div>

          {/* Disclaimer */}
          <div className="text-center mt-8">
            <p className="text-sm xl:text-base text-gray-600 italic">
              {t('activities.disclaimer')}
            </p>
          </div>
        </div>
      </section>

      {/* Discover Section - 3 Photo Cards */}
      <section className="design-section-gap rounded-3xl overflow-hidden bg-[#faf8f6]">
        <div className="max-w-6xl mx-auto design-content-pad lg:py-20">
          <h2 className="font-poppins text-3xl xl:text-4xl font-bold text-gray-900 text-center mb-12 observe-me slide-up">
            {t('discover.heading')}
          </h2>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              {
                src: '/design-images/uploads/1788861484157-szzks0-0018ds2_4956.jpg',
                alt: t('discover.imageAlt1'),
                text: t.rich('discover.text1', { strong: (chunks) => <strong>{chunks}</strong> }),
              },
              {
                src: '/design-images/uploads/1788861484490-2ad7lt-11.jpg',
                alt: t('discover.imageAlt2'),
                text: t.rich('discover.text2', { strong: (chunks) => <strong>{chunks}</strong> }),
              },
              {
                src: '/design-images/uploads/1788861484506-92k8gw-chatgpt-image-sep-8-2026-12_54_14-pm.jpg',
                alt: t('discover.imageAlt3'),
                text: t.rich('discover.text3', { strong: (chunks) => <strong>{chunks}</strong> }),
              },
            ].map((item, i) => (
              <div
                key={i}
                className="observe-me slide-up bg-white rounded-3xl shadow-lg overflow-hidden hover:shadow-2xl transition-all duration-500"
                style={{ animationDelay: `${i * 0.15}s` }}
              >
                <div className="relative h-64 overflow-hidden">
                  <Image
                    src={item.src}
                    alt={item.alt}
                    fill
                    className="object-cover hover:scale-105 transition-transform duration-700"
                  />
                </div>
                <div className="p-6">
                  <p className="text-gray-600 leading-relaxed">{item.text}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Slow Down Section - Asymmetric Layout (Image 3/5, Text 2/5) */}
      <section className="grid lg:grid-cols-5 min-h-screen design-section-gap rounded-3xl overflow-hidden">
        {/* Text Side - 2 columns */}
        <div className="lg:col-span-2 flex items-center justify-center design-content-pad lg:p-12 xl:p-16 bg-[#faf8f6] order-2 lg:order-1">
          <div className="max-w-lg observe-me slide-right">
            <h2 className="font-poppins text-[clamp(2rem,3.5vw,3.5rem)] font-bold mb-6 leading-[1.1] text-gray-900">
              {t('slowDown.heading')}
            </h2>
            <div className="w-16 h-1 bg-[#801515] mb-6" />

            <div className="space-y-4 mb-8 text-gray-700 text-base xl:text-lg leading-relaxed">
              <p>{t.rich('slowDown.p1', { strong: (chunks) => <strong>{chunks}</strong> })}</p>
              <p>{t.rich('slowDown.p2', { strong: (chunks) => <strong>{chunks}</strong> })}</p>
              <p>{t('slowDown.p3')}</p>
            </div>
          </div>
        </div>

        {/* Image Side - 3 columns */}
        <div className="lg:col-span-3 relative h-[50vh] lg:h-auto overflow-hidden bg-gray-100 order-1 lg:order-2 observe-me slide-left">
          <Image
            src="/design-images/uploads/1788863165221-w7h4zb-img_0213-hdr.jpg"
            alt={t('slowDown.imageAlt')}
            fill
            className="object-cover"
          />
        </div>
      </section>

      {/* What's Included Section - Asymmetric Layout (Image 3/5, Text 2/5) */}
      <section className="grid lg:grid-cols-5 min-h-screen design-section-gap rounded-3xl overflow-hidden">
        {/* Image Side - 3 columns */}
        <div className="lg:col-span-3 relative h-[50vh] lg:h-auto overflow-hidden bg-gray-100 observe-me slide-left">
          <Image
            src="/design-images/uploads/1788782233727-txrim3-c1_06.jpg"
            alt={t('included.imageAlt')}
            fill
            className="object-cover"
          />
        </div>

        {/* Text Side - 2 columns */}
        <div className="lg:col-span-2 flex items-center justify-center design-content-pad lg:p-12 xl:p-16 bg-[#faf8f6]">
          <div className="max-w-lg observe-me slide-right">
            <h2 className="font-poppins text-[clamp(2rem,3.5vw,3.5rem)] font-bold mb-6 leading-[1.1] text-gray-900">
              {t('included.heading')}
            </h2>
            <div className="w-16 h-1 bg-[#801515] mb-6" />

            {/* Benefits List */}
            <ul className="space-y-3 mb-8 text-gray-700">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <li key={i} className="flex gap-3">
                  <span className="text-[#801515] font-bold flex-shrink-0">•</span>
                  <span className="text-base xl:text-lg leading-relaxed">
                    {t(`included.item${i}`)}
                  </span>
                </li>
              ))}
            </ul>

            {/* Notes Section */}
            <div className="space-y-4 mb-8 text-gray-700 text-base xl:text-lg leading-relaxed">
              <p>{t('included.note1')}</p>
              <p>{t('included.note2')}</p>
              <p className="font-semibold text-gray-800">{t('included.important')}</p>
              <p>{t('included.cta')}</p>
            </div>

            {/* CTA Button */}
            <div>
              <Link
                href={OFFER_BOOKING_HREF}
                className="group px-[26px] py-5 xl:py-6 bg-[#801515] hover:bg-[#601010] text-white font-serif text-xl xl:text-2xl rounded-full transition-all duration-500 inline-flex items-center gap-3 shadow-lg hover:shadow-xl hover:shadow-[#801515]/30"
              >
                {t('included.button')}
                <ArrowRight className="w-7 h-7 group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="design-section-gap rounded-3xl overflow-hidden bg-white">
        <div className="max-w-3xl mx-auto design-content-pad lg:py-16">
          <div className="observe-me slide-up">
            <h2 className="font-poppins text-[clamp(2.25rem,3.5vw,4rem)] font-bold text-center mb-12">
              <span className="text-[#801515]">{t('faq.heading')}</span>
              <br />
              <span className="text-gray-800 text-3xl md:text-[clamp(1.75rem,2.8vw,3rem)] font-normal mt-3 block">
                {t('faq.subheading')}
              </span>
            </h2>
            <FAQAccordion />
          </div>
        </div>
      </section>

      {/* Terms & Conditions Section */}
      <section className="design-section-gap rounded-3xl overflow-hidden bg-[#faf5f5]">
        <div className="max-w-3xl mx-auto design-content-pad lg:py-16">
          <div className="observe-me slide-up">
            <h2 className="font-poppins text-2xl font-bold text-gray-900 mb-8">
              {t('terms.heading')}
            </h2>
            <ul className="space-y-3 text-gray-700 leading-relaxed">
              {terms.map((term, i) => (
                <li key={i} className="flex gap-3">
                  <span className="text-[#801515] font-bold flex-shrink-0">•</span>
                  <span>{term}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      <Footer />
    </main>
  );
}
